import { useEffect, useRef, useState } from 'react'
import { useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet.locatecontrol'
import 'leaflet.locatecontrol/dist/L.Control.Locate.min.css'
import { makeStyles } from '@material-ui/core/styles'
import IconButton from '@material-ui/core/IconButton'
import GpsFixedIcon from '@material-ui/icons/GpsFixed'
import GpsNotFixedIcon from '@material-ui/icons/GpsNotFixed'
import { useTranslation } from 'react-i18next'

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'absolute',
    zIndex: 1000,
  },
  defaultPosition: {
    top: 120,
    right: 12,
  },
  bottomRightPosition: {
    bottom: 10,
    right: 12,
  },
  btn: {
    width: 36,
    height: 36,
    backgroundColor: '#fff',
    border: '1.5px solid rgba(0,0,0,0.1)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    borderRadius: 12,
    color: '#9CA3AF',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#fff',
      color: '#232C57',
      boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
      borderColor: 'rgba(35, 44, 87, 0.25)',
    },
  },
  btnActive: {
    backgroundColor: '#232C57',
    color: '#fff',
    borderColor: '#232C57',
    boxShadow: '0 2px 8px rgba(35, 44, 87, 0.4)',
    '&:hover': {
      backgroundColor: '#1a2145',
      color: '#fff',
      borderColor: '#1a2145',
    },
  },
  icon: {
    fontSize: 19,
  },
}))

export default function LeafletLocateControl({ position = 'default', showDefaultControl = false }) {
  const classes = useStyles()
  const map = useMap()
  const { t } = useTranslation()
  const lcRef = useRef(null)
  const isActiveRef = useRef(false)
  const buttonRef = useRef(null)
  const [isActive, setIsActive] = useState(false)

  useEffect(() => {
    if (!map) return;

    const updateButtonState = () => {
      if (!buttonRef.current) return;
      const button = buttonRef.current.querySelector('button');
      if (!button) return;

      if (isActiveRef.current) {
        button.classList.add(classes.active);
        button.title = t('Stop tracking my location');
      } else {
        button.classList.remove(classes.active);
        button.title = t('Show my location');
      }
    };

    if (!lcRef.current) {
      const lc = L.control.locate({
        position: 'topleft',
        drawCircle: true,
        showPopup: false,
        showCompass: true,
        keepCurrentZoomLevel: false,
        setView: 'untilPanOrZoom',
        circleStyle: {
          color: '#1976d2',
          fillColor: '#1976d2',
          fillOpacity: 0.15,
          weight: 2,
        },
        markerStyle: {
          radius: 8,
          fillColor: '#1976d2',
          color: '#fff',
          fillOpacity: 1,
          weight: 2,
          opacity: 1
        },
        circlePadding: [0, 0],
        locateOptions: {
          enableHighAccuracy: true,
          watch: true
        },
        strings: {
          title: t('Show me where I am'),
          popup: t('You are within {distance} {unit} from this point'),
          outsideMapBoundsMsg: t('You seem located outside the boundaries of the map'),
        },
        onActivate: () => {
          isActiveRef.current = true;
          setIsActive(true);
          updateButtonState();
        },
        onDeactivate: () => {
          isActiveRef.current = false;
          setIsActive(false);
          updateButtonState();
        }
      });

      lcRef.current = lc;

      // Override _unload to add null check - prevents errors when map is destroyed during logout
      const originalUnload = lc._unload.bind(lc);
      lc._unload = function() {
        if (this._map) {
          originalUnload();
        }
      };

      // Add the control to the map
      if (showDefaultControl) {
        // Just add the control if we want to show the default
        lc.addTo(map);
      } else {
        // Add the control but hide the default UI
        lc.addTo(map);
        
        // Hide the default locate control button completely
        setTimeout(() => {
          const locateButtons = document.querySelectorAll('.leaflet-control-locate');
          locateButtons.forEach(button => {
            // Don't hide our custom control container
            if (!button.querySelector('.MuiPaper-root')) {
              button.style.display = 'none';
            }
          });
        }, 100);
      }
    }

    updateButtonState();

    // Cleanup
    return () => {
      if (lcRef.current) {
        try {
          // Don't call stop() during cleanup - it can fail if the map is being destroyed
          // Just remove the control if the map still exists
          if (map && lcRef.current._map) {
            map.removeControl(lcRef.current);
          }
        } catch (err) {
          // Silently ignore cleanup errors during unmount
        }
        lcRef.current = null;
      }
    };
  }, [map, t, classes.active]);

  const handleLocateClick = () => {
    if (!lcRef.current) return

    
    if (isActiveRef.current) {
      lcRef.current.stop()
      // Force update the state in case the event doesn't fire
      isActiveRef.current = false
      setIsActive(false)
    } else {
      lcRef.current.start()
      // Force update the state in case the event doesn't fire
      isActiveRef.current = true
      setIsActive(true)
    }
  }
  
  return (
    <div ref={buttonRef} className={`${classes.root} ${position === 'bottomRight' ? classes.bottomRightPosition : classes.defaultPosition}`}>
      <IconButton
        onClick={handleLocateClick}
        title={isActive ? t('Stop tracking my location') : t('Show my location')}
        className={`${classes.btn} ${isActive ? classes.btnActive : ''}`}
        size="small"
      >
        {isActive ? (
          <GpsFixedIcon className={classes.icon} />
        ) : (
          <GpsNotFixedIcon className={classes.icon} />
        )}
      </IconButton>
    </div>
  )
}
